import Editor from '@monaco-editor/react';
import { Check, Database, Eye, Play, Plus, Shield, Table, X, Zap } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useIsDark } from '../../contexts/ThemeContext';
import { useWorkflowContext } from '../../contexts/WorkflowContext';
import { useNodeValidation, useNodeValidationPattern } from '../../hooks/useNodeValidation';
import { evaluateExpression } from '../../lib/expression-evaluator';
import type { DatabaseNodeConfig } from '../../types/nodes';
import { IntegrationsNodeData } from '../../types/nodes';
import { LazyJsonPreview } from '../shared/LazyJsonPreview';
import { ValidationResultDisplay } from '../shared/ValidationResultDisplay';
import { VariablePicker } from '../VariablePicker';

interface DatabaseConfigProps {
	nodeId: string;
	data: IntegrationsNodeData;
	onChange: (data: IntegrationsNodeData) => void;
	// Wizard state callbacks for parent component
	onWizardStateChange?: (state: {
		wizardMode: boolean;
		currentStep: number;
		totalSteps: number;
		canProceedToNext: boolean;
		onPreviousStep: () => void;
		onNextStep: () => void;
		onCompleteWizard: () => Promise<void>;
		validationAttempted: boolean;
		validationFailed: boolean;
		isCompletingSetup: boolean;
	}) => void;
	onValidationRequest?: (validate: () => Promise<boolean>) => void;
}

const defaultConfig: DatabaseNodeConfig = {
	connection: {
		type: 'postgres',
		host: 'localhost',
		port: 5432,
		database: '',
		username: '',
		password: '',
		useSSL: false,
	},
	// Default to guided mode but allow advanced SQL
	mode: 'READ_BUILDER',
	operation: 'read',
	designMode: 'guided',
	readBuilder: {
		table: '',
		filters: [],
		orderBy: [],
		limit: 100,
	},
	insertConfig: {
		table: '',
		rows: [{}],
		conflictHandling: {
			enabled: false,
			conflictColumns: [],
			updateColumns: [],
		},
	},
	createTableConfig: {
		table: '',
		columns: [
			{ name: 'id', type: 'integer', primaryKey: true, nullable: false },
			{ name: 'name', type: 'varchar(255)', nullable: false },
			{ name: 'created_at', type: 'timestamptz', nullable: false, default: 'NOW()' },
		],
		indexes: [],
		ifNotExists: true,
		// NEW: Schema alteration options (default to disabled for backward compatibility)
		allowSchemaAlteration: false,
		schemaValidationMode: 'STRICT' as const,
		dryRun: false,
	},
	options: {
		dryRun: false,
	},
};

// Simplified to PostgreSQL only - removed unused DATABASE_TYPE constant

const OPERATIONS = [
	{
		id: 'read',
		title: 'Query Data',
		description: 'Retrieve data from tables with filters and sorting',
		detailedDescription:
			'Perfect for fetching user records, generating reports, or getting specific data based on conditions',
		icon: '🔍',
		color: 'blue',
		difficulty: 'Easy',
		useCase: 'Most common operation - great for beginners',
	},
	{
		id: 'insert',
		title: 'Insert Rows',
		description: 'Add new records to a table',
		detailedDescription:
			'Ideal for saving form submissions, creating new user accounts, or logging events',
		icon: '➕',
		color: 'green',
		difficulty: 'Easy',
		useCase: 'Perfect for saving workflow data',
	},
	{
		id: 'create-table',
		title: 'Create Table',
		description: 'Design and create new database tables',
		detailedDescription:
			'Advanced operation for setting up new data structures with columns, types, and constraints',
		icon: '🏗️',
		color: 'purple',
		difficulty: 'Advanced',
		useCase: 'For database administrators and advanced users',
	},
	{
		id: 'upsert',
		title: 'Insert or Update',
		description: 'Smart insert that updates existing records',
		detailedDescription:
			'Automatically handles conflicts - inserts new records or updates existing ones based on unique constraints',
		icon: '🔄',
		color: 'orange',
		difficulty: 'Intermediate',
		useCase: 'Great for syncing data from external sources',
	},
] as const;

const QUERY_TEMPLATES: Array<{
	name: string;
	query: string;
	parameters: Record<string, string>;
	description: string;
}> = [
	{
		name: 'User Lookup',
		query: 'SELECT id, name, email, created_at FROM users WHERE id = :userId',
		parameters: { userId: '{{userId}}' },
		description: 'Find a user by ID',
	},
	{
		name: 'Order History',
		query: 'SELECT o.*, p.name as product_name FROM orders o JOIN products p ON o.product_id = p.id WHERE o.user_id = :userId ORDER BY o.created_at DESC LIMIT :limit',
		parameters: { userId: '{{userId}}', limit: '10' },
		description: 'Get user order history with product details',
	},
	{
		name: 'Sales Summary',
		query: 'SELECT DATE(created_at) as date, COUNT(*) as order_count, SUM(total) as revenue FROM orders WHERE created_at >= :startDate AND created_at <= :endDate GROUP BY DATE(created_at) ORDER BY date',
		parameters: { startDate: '{{startDate}}', endDate: '{{endDate}}' },
		description: 'Daily sales summary for date range',
	},
	{
		name: 'Inventory Check',
		query: 'SELECT id, name, stock_quantity, price FROM products WHERE stock_quantity < :threshold ORDER BY stock_quantity ASC',
		parameters: { threshold: '{{minStock}}' },
		description: 'Find products with low inventory',
	},
];

interface QueryResult {
	rows: any[];
	rowCount: number;
	columns: string[];
	executionTime: number;
}

export function DatabaseConfig({
	nodeId,
	data,
	onChange,
	onWizardStateChange,
	onValidationRequest,
}: DatabaseConfigProps) {
	// Validation pattern implementation
	const { validationAttempted, validationFailed, performValidation, handleCompleteWizard } =
		useNodeValidationPattern(nodeId, data);

	// Validation display state
	const { isValidating, lastValidationResult } = useNodeValidation();

	const { getAvailableFields, getFlattenedFields } = useWorkflowContext();

	const [config, setConfig] = useState<DatabaseNodeConfig>(() => {
		const existingConfig = data.config as Partial<DatabaseNodeConfig>;
		return {
			...defaultConfig,
			...existingConfig,
			connection: {
				...defaultConfig.connection,
				...existingConfig?.connection,
			},
		};
	});

	// Wizard state management
	const [currentStep, setCurrentStep] = useState(1);
	const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
	const [isWizardCompleted, setIsWizardCompleted] = useState(false);
	const totalSteps = 3;

	const [isTestingConnection, setIsTestingConnection] = useState(false);
	const [connectionStatus, setConnectionStatus] = useState<{
		success: boolean;
		message: string;
	} | null>(null);
	const [isRunningQuery, setIsRunningQuery] = useState(false);
	const [queryResult, setQueryResult] = useState<QueryResult | null>(null);
	const [queryError, setQueryError] = useState<string | null>(null);
	const [resultViewMode, setResultViewMode] = useState<'table' | 'json'>('table');
	const [availableConnections, setAvailableConnections] = useState<any[]>([]);
	const [loadingConnections, setLoadingConnections] = useState(false);

	// Schema validation state
	const [isValidatingSchema, setIsValidatingSchema] = useState(false);
	const [schemaValidationResult, setSchemaValidationResult] = useState<any>(null);
	const [showSchemaModal, setShowSchemaModal] = useState(false);
	const [schemaViewMode, setSchemaViewMode] = useState<'visual' | 'json'>('visual');

	// SQL paste mode state
	const [tableDesignMode, setTableDesignMode] = useState<'manual' | 'sql'>('manual');
	const [sqlInput, setSqlInput] = useState<string>('');
	const [isParsingSql, setIsParsingSql] = useState(false);
	const [sqlParseError, setSqlParseError] = useState<string | null>(null);

	const isDark = useIsDark();

	// Memoize available fields to prevent expensive re-computations
	const availableFields = useMemo(() => getAvailableFields(nodeId), [getAvailableFields, nodeId]);
	const flattenedFields = useMemo(() => getFlattenedFields(nodeId), [getFlattenedFields, nodeId]);

	// Load available database connections on component mount
	useEffect(() => {
		let isMounted = true; // Cleanup flag to prevent state updates after unmount
		let abortController = new AbortController(); // Cancel pending requests

		const loadConnections = async () => {
			if (!isMounted) return;

			setLoadingConnections(true);
			try {
				// Get auth token for authenticated request
				const token = sessionStorage.getItem('auth_token');
				if (!token) {
					console.warn('No auth token available for database connections');
					if (isMounted) setAvailableConnections([]);
					return;
				}

				const response = await fetch('/api/v1/db/connections', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${token}`, // SECURITY: Add auth header
					},
					signal: abortController.signal, // Allow request cancellation
				});

				if (!isMounted) return; // Component unmounted during request

				if (response.ok) {
					const result = await response.json();
					setAvailableConnections(result.connections || []);
				} else if (response.status === 401) {
					console.warn('Authentication required for database connections');
					setAvailableConnections([]);
				} else {
					console.error('Failed to load database connections:', response.statusText);
					setAvailableConnections([]);
				}
			} catch (error) {
				if (isMounted && (!(error instanceof Error) || error.name !== 'AbortError')) {
					console.error('Failed to load database connections:', error);
					setAvailableConnections([]);
				}
			} finally {
				if (isMounted) {
					setLoadingConnections(false);
				}
			}
		};

		loadConnections();

		// Comprehensive cleanup function to prevent memory leaks
		return () => {
			isMounted = false;
			abortController.abort(); // Cancel any pending requests

			// Clear large data objects to free memory
			setQueryResult(null);
			setSchemaValidationResult(null);
			setAvailableConnections([]);
		};
	}, []); // Remove dependencies to avoid issues with function declarations

	// Memoize mock context to prevent expensive re-computations on every render
	const mockContext = useMemo(() => {
		return flattenedFields.reduce((acc, field) => {
			acc[field.id] = getMockValue(field.type, field.label);
			return acc;
		}, {} as Record<string, any>);
	}, [flattenedFields]);

	// Memoize enhanced mock context with default values
	const enhancedMockContext = useMemo(
		() => ({
			userId: '123',
			userStatus: 'active',
			startDate: '2024-01-01',
			endDate: '2024-01-31',
			minStock: '10',
			...mockContext,
		}),
		[mockContext]
	);

	function getMockValue(type: string, label: string): any {
		const lowerLabel = label.toLowerCase();

		// Smart mock values based on field names
		if (lowerLabel.includes('id')) return Math.floor(Math.random() * 1000) + 1;
		if (lowerLabel.includes('email')) return 'user@example.com';
		if (lowerLabel.includes('name')) return 'Sample User';
		if (lowerLabel.includes('status')) return 'active';
		if (lowerLabel.includes('date')) return '2024-01-15';
		if (lowerLabel.includes('amount') || lowerLabel.includes('price')) return '99.99';

		switch (type) {
			case 'string':
				return 'Sample Text';
			case 'number':
				return 42;
			case 'boolean':
				return true;
			case 'object':
				return { key: 'value' };
			case 'array':
				return ['item1', 'item2'];
			default:
				return 'Sample';
		}
	}

	// Immediate change handler for critical updates (dropdowns, checkboxes, etc.)
	const handleChange = useCallback(
		(newConfig: Partial<DatabaseNodeConfig>) => {
			const updatedConfig = { ...config, ...newConfig };
			setConfig(updatedConfig);
			onChange({
				...data,
				config: updatedConfig,
			});
		},
		[config, data, onChange]
	);

	// Debounced change handler for text inputs and frequent updates
	const debouncedHandleChange = useDebouncedCallback((newConfig: Partial<DatabaseNodeConfig>) => {
		const updatedConfig = { ...config, ...newConfig };
		setConfig(updatedConfig);
		onChange({
			...data,
			config: updatedConfig,
		});
	}, 300); // 300ms debounce delay

	// Wizard step validation
	const validateStep = (step: number): boolean => {
		switch (step) {
			case 1: // Connection step
				return !!config.connection?.secretRef;
			case 2: // Design step
				if (!config.operation) return false;

				switch (config.operation) {
					case 'read':
						if (config.designMode === 'guided') {
							return !!config.readBuilder?.table;
						} else {
							return !!(config.query && config.query.trim());
						}
					case 'insert':
					case 'upsert':
						return !!config.insertConfig?.table;
					case 'create-table':
						const isValid = !!(
							config.createTableConfig?.table &&
							config.createTableConfig?.columns &&
							config.createTableConfig.columns.length > 0
						);
						return isValid;
					default:
						return false;
				}
			case 3: // Preview step (always valid for navigation)
				return true;
			default:
				return false;
		}
	};

	// Wizard navigation
	const canProceedToNext = validateStep(currentStep);

	// Register validation function with parent
	useEffect(() => {
		if (onValidationRequest) {
			onValidationRequest(performValidation);
		}
	}, [onValidationRequest, performValidation]);

	const testConnection = async () => {
		setIsTestingConnection(true);
		setConnectionStatus(null);

		const abortController = new AbortController();

		try {
			// Get auth token for authenticated request
			const token = sessionStorage.getItem('auth_token');
			if (!token) {
				setConnectionStatus({
					success: false,
					message: 'Authentication required to test database connections',
				});
				return;
			}

			// Call workflow database connection test API
			const response = await fetch('/api/v1/db/test-workflow-connection', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token}`, // SECURITY: Add auth header
				},
				body: JSON.stringify({
					connectionMode: config.connection.mode || 'local-secrets',
					type: 'postgres',
					secretPath:
						config.connection.mode === 'aws-secret'
							? config.connection.secretRef
							: undefined,
					localSecretFile:
						config.connection.mode === 'local-secrets'
							? config.connection.secretRef
							: undefined,
					connectionName: config.connection.secretName,
				}),
				signal: abortController.signal, // Allow request cancellation
			});

			if (response.ok) {
				const result = await response.json();
				setConnectionStatus({
					success: result.success,
					message: result.message,
				});
			} else {
				// Handle different HTTP error status codes
				let errorMessage = 'Connection test failed';

				if (response.status === 400) {
					const error = await response.json();
					errorMessage = error.message || 'Invalid connection parameters';
				} else if (response.status === 401) {
					errorMessage = 'Authentication required';
				} else if (response.status === 500) {
					errorMessage = 'Server error during connection test';
				} else {
					try {
						const error = await response.json();
						errorMessage =
							error.message || `HTTP ${response.status}: ${response.statusText}`;
					} catch {
						errorMessage = `HTTP ${response.status}: ${response.statusText}`;
					}
				}

				setConnectionStatus({
					success: false,
					message: errorMessage,
				});
			}
		} catch (error) {
			// Handle abort errors gracefully
			if (error instanceof Error && error.name === 'AbortError') {
				console.log('Connection test was cancelled');
				return;
			}

			// Handle network errors (server not running, etc.)
			let networkErrorMessage = 'Cannot connect to server';

			if (error instanceof Error) {
				if (error.message.includes('Failed to fetch')) {
					networkErrorMessage = 'Backend server is not running or unreachable';
				} else if (error.message.includes('ECONNREFUSED')) {
					networkErrorMessage = 'Connection refused - backend server may be down';
				} else {
					networkErrorMessage = `Network error: ${error.message}`;
				}
			}

			setConnectionStatus({
				success: false,
				message: networkErrorMessage,
			});
		} finally {
			setIsTestingConnection(false);
		}
	};

	const runQuery = async () => {
		setIsRunningQuery(true);
		setQueryResult(null);
		setQueryError(null);

		try {
			// Resolve parameters with mock context
			const resolvedParams: Record<string, any> = {};
			Object.entries(config.parameters || {}).forEach(([key, tokenValue]) => {
				resolvedParams[key] = evaluateExpression(tokenValue, enhancedMockContext);
			});

			// Simulate query execution
			await new Promise(resolve => setTimeout(resolve, 1500));

			// Generate mock result data based on query type
			const mockRows = generateMockQueryResult(config.query || '', resolvedParams);

			setQueryResult({
				rows: mockRows,
				rowCount: mockRows.length,
				columns: mockRows.length > 0 ? Object.keys(mockRows[0]) : [],
				executionTime: Math.random() * 200 + 50, // 50-250ms
			});
		} catch (error) {
			setQueryError(error instanceof Error ? error.message : 'Query execution failed');
		} finally {
			setIsRunningQuery(false);
		}
	};

	function generateMockQueryResult(query: string, _params: Record<string, any>): any[] {
		const lowerQuery = query.toLowerCase();

		if (lowerQuery.includes('select') && lowerQuery.includes('users')) {
			return [
				{
					id: 123,
					name: 'John Doe',
					email: 'john@example.com',
					status: 'active',
					created_at: '2024-01-15T10:30:00Z',
				},
				{
					id: 124,
					name: 'Jane Smith',
					email: 'jane@example.com',
					status: 'active',
					created_at: '2024-01-16T14:20:00Z',
				},
			];
		} else if (lowerQuery.includes('orders')) {
			return [
				{
					id: 1001,
					user_id: 123,
					product_name: 'Widget A',
					total: 99.99,
					created_at: '2024-01-20T09:15:00Z',
				},
				{
					id: 1002,
					user_id: 123,
					product_name: 'Widget B',
					total: 149.99,
					created_at: '2024-01-21T16:45:00Z',
				},
			];
		} else if (lowerQuery.includes('products')) {
			return [
				{ id: 1, name: 'Widget A', stock_quantity: 5, price: 99.99 },
				{ id: 2, name: 'Widget B', stock_quantity: 2, price: 149.99 },
			];
		} else if (lowerQuery.includes('group by')) {
			return [
				{ date: '2024-01-20', order_count: 15, revenue: 2547.85 },
				{ date: '2024-01-21', order_count: 23, revenue: 3421.6 },
			];
		}

		// Default mock result
		return [
			{ id: 1, result: 'Sample Result 1', value: 42 },
			{ id: 2, result: 'Sample Result 2', value: 84 },
		];
	}

	const validateQuery = useCallback(() => {
		// Different validation based on operation type
		switch (config.operation) {
			case 'read':
				// For guided mode, validate the query builder
				if (config.designMode === 'guided') {
					if (!config.readBuilder?.table || !config.readBuilder.table.trim()) {
						setQueryError('Table name is required');
						return false;
					}
					setQueryError(null);
					return true;
				} else {
					// For advanced mode, validate the raw SQL query
					if (!config.query || !config.query.trim()) {
						setQueryError('Query cannot be empty');
						return false;
					}
				}
				break;

			case 'create-table':
				// For create table, validate table name and columns
				if (!config.createTableConfig?.table || !config.createTableConfig.table.trim()) {
					setQueryError('Table name is required');
					return false;
				}
				if (
					!config.createTableConfig?.columns ||
					config.createTableConfig.columns.length === 0
				) {
					setQueryError('At least one column is required');
					return false;
				}
				setQueryError(null);
				console.log(
					`✅ Create table validation passed: Table = ${config.createTableConfig.table}, Columns = ${config.createTableConfig.columns.length}`
				);
				return true;

			case 'insert':
			case 'upsert':
				// For insert/upsert, validate table name
				if (!config.insertConfig?.table || !config.insertConfig.table.trim()) {
					setQueryError('Table name is required');
					return false;
				}
				setQueryError(null);
				console.log(
					`✅ ${config.operation} validation passed: Table = ${config.insertConfig.table}`
				);
				return true;

			default:
				setQueryError('Please select an operation');
				return false;
		}

		// For advanced mode read queries, validate the raw SQL
		if (!config.query || !config.query.trim()) {
			setQueryError('Query cannot be empty');
			return false;
		}

		const queryUpper = config.query.toUpperCase();
		if (
			queryUpper.includes('DROP') ||
			queryUpper.includes('TRUNCATE') ||
			(queryUpper.includes('DELETE') && !queryUpper.includes('WHERE'))
		) {
			setQueryError('Potentially dangerous query detected. Please review your query.');
			return false;
		}

		setQueryError(null);
		return true;
	}, [
		config.operation,
		config.designMode,
		config.readBuilder,
		config.query,
		config.createTableConfig,
		config.insertConfig,
	]);

	/**
	 * Handle schema validation for existing tables
	 * This function calls the backend to validate schema changes and shows the results to the user
	 */
	const handleSchemaValidation = async () => {
		if (!config.createTableConfig?.table) {
			setQueryError('Table name is required for schema validation');
			return;
		}

		setIsValidatingSchema(true);
		setSchemaValidationResult(null);
		setQueryError(null);

		try {
			// Format connection to trigger secret resolution in DatabaseService.execute
			const connectionWithSecrets = {
				type: config.connection.type || 'postgres',
				mode: config.connection.mode || 'local-secrets',
				secretRef: config.connection.secretRef || config.connection.secretName,
				secretName: config.connection.secretName,
				// Don't include host/password to trigger secret resolution path
			};

			const validationPayload = {
				connection: connectionWithSecrets,
				mode: 'CREATE_TABLE',
				payload: {
					table: config.createTableConfig.table,
					columns: config.createTableConfig.columns || [],
					indexes: config.createTableConfig.indexes || [],
					ifNotExists: config.createTableConfig.ifNotExists !== false,
					allowSchemaAlteration: true,
					dryRun: true, // Always use dry-run for validation
				},
				options: {
					dryRun: true,
				},
			};

			const response = await fetch('/api/v1/db/execute', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
				},
				body: JSON.stringify(validationPayload),
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(errorData.message || 'Schema validation failed');
			}

			const result = await response.json();
			setSchemaValidationResult(result);
			setShowSchemaModal(true);
		} catch (error) {
			// console.error('Schema validation error:', error);
			setQueryError(error instanceof Error ? error.message : 'Schema validation failed');
		} finally {
			setIsValidatingSchema(false);
		}
	};

	// Wizard navigation callbacks (defined after validateQuery)
	const handleNextStep = useCallback(() => {
		if (canProceedToNext && currentStep < totalSteps) {
			// Special validation for query step
			if (currentStep === 2) {
				if (!validateQuery()) {
					return; // Don't proceed if query validation fails
				}
			}

			setCompletedSteps(prev => new Set([...prev, currentStep]));
			setCurrentStep(prev => prev + 1);
		}
	}, [canProceedToNext, currentStep, totalSteps, validateQuery]);

	const handlePreviousStep = useCallback(() => {
		if (currentStep > 1) {
			setCurrentStep(prev => prev - 1);
		}
	}, [currentStep]);

	const handleCompleteWizardSetup = useCallback(async () => {
		// Save the configuration first - use current state value instead of stale closure
		setConfig(currentConfig => {
			const finalData = {
				...data,
				config: currentConfig,
			};
			onChange(finalData);
			return currentConfig; // Return unchanged config
		});

		// Mark wizard as completed to turn off wizard mode
		setIsWizardCompleted(true);

		// Call the parent completion handler (this should close the drawer)
		await handleCompleteWizard(async () => {
			// Configuration already saved above
		});
	}, [handleCompleteWizard, onChange, data]);

	// Wizard state management (defined after callbacks)
	useEffect(() => {
		if (onWizardStateChange) {
			onWizardStateChange({
				wizardMode: !isWizardCompleted, // Turn off wizard mode when completed
				currentStep,
				totalSteps,
				canProceedToNext,
				onPreviousStep: handlePreviousStep,
				onNextStep: handleNextStep,
				onCompleteWizard: handleCompleteWizardSetup,
				validationAttempted,
				validationFailed,
				isCompletingSetup: false,
			});
		}
	}, [
		onWizardStateChange,
		currentStep,
		totalSteps,
		canProceedToNext,
		handlePreviousStep,
		handleNextStep,
		handleCompleteWizardSetup,
		validationAttempted,
		validationFailed,
		isWizardCompleted,
	]);

	const loadTemplate = (template: (typeof QUERY_TEMPLATES)[0]) => {
		handleChange({
			query: template.query,
			parameters: template.parameters,
		});
		// Template loaded, stay on current step
	};

	const addParameter = () => {
		const paramName = `param${Object.keys(config.parameters || {}).length + 1}`;
		handleChange({
			parameters: {
				...config.parameters,
				[paramName]: '',
			},
		});
	};

	const removeParameter = (paramName: string) => {
		const newParams = { ...config.parameters };
		delete newParams[paramName];
		handleChange({ parameters: newParams });
	};

	const updateParameter = (oldName: string, newName: string, value: string) => {
		const newParams = { ...config.parameters };
		delete newParams[oldName];
		newParams[newName] = value;
		handleChange({ parameters: newParams });
	};

	// Filter management functions
	const addFilter = () => {
		const newFilter = {
			column: '',
			operator: '=' as any,
			value: '',
		};
		handleChange({
			readBuilder: {
				table: config.readBuilder?.table || '',
				filters: [...(config.readBuilder?.filters || []), newFilter],
				orderBy: config.readBuilder?.orderBy || [],
				limit: config.readBuilder?.limit || 100,
				offset: config.readBuilder?.offset || 0,
			},
		});
	};

	const updateFilter = useCallback(
		(index: number, updates: Partial<any>) => {
			const filters = [...(config.readBuilder?.filters || [])];
			filters[index] = { ...filters[index], ...updates };
			handleChange({
				readBuilder: {
					table: config.readBuilder?.table || '',
					filters,
					orderBy: config.readBuilder?.orderBy || [],
					limit: config.readBuilder?.limit || 100,
					offset: config.readBuilder?.offset || 0,
				},
			});
		},
		[config.readBuilder, handleChange]
	);

	// Debounced version for filter text inputs
	const debouncedUpdateFilter = useDebouncedCallback((index: number, updates: Partial<any>) => {
		setConfig(currentConfig => {
			const filters = [...(currentConfig.readBuilder?.filters || [])];
			filters[index] = { ...filters[index], ...updates };
			const updatedConfig = {
				...currentConfig,
				readBuilder: {
					table: currentConfig.readBuilder?.table || '',
					filters,
					orderBy: currentConfig.readBuilder?.orderBy || [],
					limit: currentConfig.readBuilder?.limit || 100,
					offset: currentConfig.readBuilder?.offset || 0,
				},
			};
			onChange({
				...data,
				config: updatedConfig,
			});
			return updatedConfig;
		});
	}, 300);

	const removeFilter = (index: number) => {
		const filters = [...(config.readBuilder?.filters || [])];
		filters.splice(index, 1);
		handleChange({
			readBuilder: {
				table: config.readBuilder?.table || '',
				filters,
				orderBy: config.readBuilder?.orderBy || [],
				limit: config.readBuilder?.limit || 100,
				offset: config.readBuilder?.offset || 0,
			},
		});
	};

	// Order by management functions
	const addOrderBy = () => {
		const newOrder = {
			column: '',
			direction: 'asc' as 'asc' | 'desc',
		};
		handleChange({
			readBuilder: {
				table: config.readBuilder?.table || '',
				filters: config.readBuilder?.filters || [],
				orderBy: [...(config.readBuilder?.orderBy || []), newOrder],
				limit: config.readBuilder?.limit || 100,
				offset: config.readBuilder?.offset || 0,
			},
		});
	};

	const updateOrderBy = (index: number, updates: Partial<any>) => {
		const orderBy = [...(config.readBuilder?.orderBy || [])];
		orderBy[index] = { ...orderBy[index], ...updates };
		handleChange({
			readBuilder: {
				table: config.readBuilder?.table || '',
				filters: config.readBuilder?.filters || [],
				orderBy,
				limit: config.readBuilder?.limit || 100,
				offset: config.readBuilder?.offset || 0,
			},
		});
	};

	const removeOrderBy = (index: number) => {
		const orderBy = [...(config.readBuilder?.orderBy || [])];
		orderBy.splice(index, 1);
		handleChange({
			readBuilder: {
				table: config.readBuilder?.table || '',
				filters: config.readBuilder?.filters || [],
				orderBy,
				limit: config.readBuilder?.limit || 100,
				offset: config.readBuilder?.offset || 0,
			},
		});
	};

	// Generate SQL preview for guided query builder
	const generateSqlPreview = (): string => {
		if (!config.readBuilder?.table) {
			return 'SELECT * FROM [table_name]';
		}

		let sql = `SELECT * FROM ${config.readBuilder.table}`;

		// Add WHERE clause
		if (config.readBuilder.filters && config.readBuilder.filters.length > 0) {
			const whereConditions = config.readBuilder.filters
				.filter(f => f.column)
				.map(filter => {
					if (filter.operator === 'IS NULL' || filter.operator === 'IS NOT NULL') {
						return `${filter.column} ${filter.operator}`;
					} else if (filter.operator === 'IN') {
						return `${filter.column} IN (${filter.value || 'value1, value2'})`;
					} else {
						return `${filter.column} ${filter.operator} '${filter.value || 'value'}'`;
					}
				});

			if (whereConditions.length > 0) {
				sql += ` WHERE ${whereConditions.join(' AND ')}`;
			}
		}

		// Add ORDER BY clause
		if (config.readBuilder.orderBy && config.readBuilder.orderBy.length > 0) {
			const orderClauses = config.readBuilder.orderBy
				.filter(o => o.column)
				.map(order => `${order.column} ${order.direction.toUpperCase()}`);

			if (orderClauses.length > 0) {
				sql += ` ORDER BY ${orderClauses.join(', ')}`;
			}
		}

		// Add LIMIT clause
		if (config.readBuilder.limit && config.readBuilder.limit > 0) {
			sql += ` LIMIT ${config.readBuilder.limit}`;

			if (config.readBuilder.offset && config.readBuilder.offset > 0) {
				sql += ` OFFSET ${config.readBuilder.offset}`;
			}
		}

		return sql;
	};

	// Column mapping management functions
	const addColumnMapping = () => {
		const newColumnName = `column${
			Object.keys(config.insertConfig?.rows?.[0] || {}).length + 1
		}`;
		const currentRow = config.insertConfig?.rows?.[0] || {};
		const updatedRow = {
			...currentRow,
			[newColumnName]: '',
		};

		handleChange({
			insertConfig: {
				...config.insertConfig,
				rows: [updatedRow],
			},
		});
	};

	const updateColumnName = (oldColumn: string, newColumn: string) => {
		const currentRow = config.insertConfig?.rows?.[0] || {};
		const updatedRow = { ...currentRow };

		// Get the current value
		const currentValue = currentRow[oldColumn];

		// Remove old column and add with new name
		delete updatedRow[oldColumn];
		updatedRow[newColumn] = currentValue;

		handleChange({
			insertConfig: {
				...config.insertConfig,
				rows: [updatedRow],
			},
		});
	};

	const updateColumnValue = (column: string, value: string) => {
		const currentRow = config.insertConfig?.rows?.[0] || {};
		const updatedRow = {
			...currentRow,
			[column]: value,
		};

		handleChange({
			insertConfig: {
				...config.insertConfig,
				rows: [updatedRow],
			},
		});
	};

	const removeColumnMapping = (column: string) => {
		const currentRow = config.insertConfig?.rows?.[0] || {};
		const updatedRow = { ...currentRow };
		delete updatedRow[column];

		handleChange({
			insertConfig: {
				...config.insertConfig,
				rows: [updatedRow],
			},
		});
	};

	const renderConnectionTab = () => (
		<div className="space-y-6">
			{/* Available Fields */}
			{availableFields.length > 0 && (
				<div className="p-3 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
					<h4 className="mb-2 text-sm font-medium text-blue-900 dark:text-blue-200">
						Available Fields
					</h4>
					<div className="flex flex-wrap gap-1">
						{flattenedFields.slice(0, 10).map(field => (
							<button
								key={field.id}
								onClick={() => {
									navigator.clipboard.writeText(`{{${field.id}}}`);
								}}
								className="px-2 py-1 text-xs text-blue-700 bg-white rounded border border-blue-300 transition-colors dark:text-blue-300 dark:bg-gray-700 dark:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
								title={`${field.description || field.label} - Click to copy {{${
									field.id
								}}}`}
							>
								{field.label}
							</button>
						))}
						{flattenedFields.length > 10 && (
							<span className="text-xs text-blue-600 dark:text-blue-400">
								+{flattenedFields.length - 10} more
							</span>
						)}
					</div>
				</div>
			)}

			{/* Database Connection Selection */}
			<div className="space-y-4">
				<div className="p-3 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
					<div className="flex gap-2 items-center mb-2">
						<span className="text-lg">🐘</span>
						<h4 className="text-sm font-medium text-blue-900 dark:text-blue-200">
							PostgreSQL Database Connection
						</h4>
					</div>
					<p className="text-xs text-blue-800 dark:text-blue-300">
						Select a pre-configured database connection managed by your team
					</p>
				</div>

				{/* Connection Dropdown */}
				<div className="space-y-2">
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Database Connection{' '}
						<span className="text-red-500 dark:text-red-400">*</span>
					</label>

					{loadingConnections ? (
						<div className="px-3 py-2 text-sm text-gray-500 bg-gray-100 rounded-lg dark:bg-gray-700 dark:text-gray-400">
							Loading available connections...
						</div>
					) : (
						<select
							value={config.connection.secretRef || ''}
							onChange={e => {
								const selectedConnection = availableConnections.find(
									conn =>
										conn.secretPath === e.target.value ||
										conn.localFile === e.target.value
								);

								handleChange({
									connection: {
										...config.connection,
										type: 'postgres',
										mode: selectedConnection?.secretPath
											? 'aws-secret'
											: 'local-secrets',
										secretRef: e.target.value,
										secretName: selectedConnection?.name,
										// Clear old credential fields
										host: undefined,
										port: undefined,
										database: undefined,
										username: undefined,
										password: undefined,
									},
								});
							}}
							className="px-3 py-2 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
						>
							<option value="">Select a database connection...</option>
							{availableConnections.map(connection => (
								<option
									key={connection.id}
									value={connection.secretPath || connection.localFile}
								>
									{connection.name} - {connection.description}
								</option>
							))}
						</select>
					)}

					{availableConnections.length === 0 && !loadingConnections && (
						<div className="p-3 text-sm text-orange-800 bg-orange-50 rounded-lg border border-orange-200 dark:bg-orange-900/20 dark:border-orange-800 dark:text-orange-200">
							No database connections available. Contact your administrator to set up
							database access.
						</div>
					)}
				</div>

				{/* Connection Info Display */}
				{config.connection.secretRef && (
					<div className="p-3 bg-green-50 rounded-lg border border-green-200 dark:bg-green-900/20 dark:border-green-800">
						<div className="flex gap-2 items-start">
							<Shield className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
							<div>
								<div className="text-sm font-medium text-green-900 dark:text-green-200">
									Secure Connection Selected
								</div>
								<div className="mt-1 text-xs text-green-800 dark:text-green-300">
									{config.connection.secretName} - Credentials managed securely
								</div>
							</div>
						</div>
					</div>
				)}
			</div>

			{/* Test Connection */}
			<div className="pt-4 border-t border-gray-200 dark:border-gray-700">
				<button
					onClick={testConnection}
					disabled={isTestingConnection || !config.connection.secretRef}
					className="flex gap-2 items-center px-3 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-md transition-colors dark:text-teal-300 dark:bg-teal-900/20 dark:border-teal-800 hover:bg-teal-100 dark:hover:bg-teal-900/30 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-500 disabled:cursor-not-allowed"
				>
					<Database className="w-3 h-3" />
					{isTestingConnection ? 'Testing...' : 'Test Connection'}
				</button>

				{connectionStatus && (
					<div
						className={`mt-3 p-3 rounded-lg border ${
							connectionStatus.success
								? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200'
								: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
						}`}
					>
						<div className="flex gap-2 items-center">
							{connectionStatus.success ? (
								<div className="flex justify-center items-center w-4 h-4 bg-green-500 rounded-full dark:bg-green-600">
									<div className="w-2 h-2 bg-white rounded-full" />
								</div>
							) : (
								<div className="flex justify-center items-center w-4 h-4 bg-red-500 rounded-full dark:bg-red-600">
									<div className="w-1 h-1 bg-white rounded-full" />
								</div>
							)}
							<span className="text-sm font-medium">{connectionStatus.message}</span>
						</div>
					</div>
				)}
			</div>
		</div>
	);

	const renderDesignTab = () => (
		<div className="space-y-6">
			{/* Operation Selector */}
			<div className="space-y-4">
				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						What do you want to do?
					</label>
					<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
						Choose the database operation that best fits your workflow needs
					</p>
				</div>

				<div className="grid grid-cols-1 gap-4">
					{OPERATIONS.map(operation => (
						<button
							key={operation.id}
							onClick={() =>
								handleChange({
									operation: operation.id as any,
									mode:
										operation.id === 'read'
											? 'READ_BUILDER'
											: operation.id === 'insert'
											? 'INSERT'
											: operation.id === 'create-table'
											? 'CREATE_TABLE'
											: 'UPSERT',
								})
							}
							className={`p-4 border rounded-xl text-left transition-all duration-200 hover:shadow-md ${
								config.operation === operation.id
									? `border-${operation.color}-500 dark:border-${operation.color}-400 bg-${operation.color}-50 dark:bg-${operation.color}-900/20 shadow-md`
									: 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
							}`}
						>
							<div className="flex gap-4 items-start">
								<div className="flex-shrink-0">
									<span className="text-3xl">{operation.icon}</span>
								</div>
								<div className="flex-1 min-w-0">
									<div className="flex gap-2 items-center mb-1">
										<div
											className={`text-base font-semibold ${
												config.operation === operation.id
													? `text-${operation.color}-700 dark:text-${operation.color}-300`
													: 'text-gray-900 dark:text-gray-100'
											}`}
										>
											{operation.title}
										</div>
										<span
											className={`px-2 py-0.5 text-xs font-medium rounded-full ${
												operation.difficulty === 'Easy'
													? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
													: operation.difficulty === 'Intermediate'
													? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
													: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
											}`}
										>
											{operation.difficulty}
										</span>
									</div>
									<div
										className={`text-sm mb-2 ${
											config.operation === operation.id
												? `text-${operation.color}-600 dark:text-${operation.color}-400`
												: 'text-gray-600 dark:text-gray-400'
										}`}
									>
										{operation.description}
									</div>
									<div className="text-xs font-medium text-gray-500 dark:text-gray-500">
										💡 {operation.useCase}
									</div>
									{config.operation === operation.id && (
										<div className="mt-2 text-xs italic text-gray-600 dark:text-gray-400">
											{operation.detailedDescription}
										</div>
									)}
								</div>
							</div>
						</button>
					))}
				</div>
			</div>

			{/* Design Mode Tabs (for read operation) */}
			{config.operation === 'read' && (
				<div className="space-y-4">
					<div className="border-b border-gray-200 dark:border-gray-700">
						<nav className="flex -mb-px space-x-8">
							{[
								{ id: 'guided', label: 'Guided (Recommended)', icon: '🎯' },
								{ id: 'advanced', label: 'Advanced SQL', icon: '⚡' },
							].map(tab => (
								<button
									key={tab.id}
									onClick={() => handleChange({ designMode: tab.id as any })}
									className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${
										config.designMode === tab.id
											? 'border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400'
											: 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
									}`}
								>
									<span>{tab.icon}</span>
									{tab.label}
								</button>
							))}
						</nav>
					</div>

					{/* Tab Content */}
					{config.designMode === 'guided'
						? renderGuidedQueryBuilder()
						: renderAdvancedSqlEditor()}
				</div>
			)}

			{/* Operation-specific configuration */}
			{config.operation === 'insert' && renderInsertConfiguration()}
			{config.operation === 'create-table' && renderCreateTableConfiguration()}
			{config.operation === 'upsert' && renderUpsertConfiguration()}
		</div>
	);

	const renderGuidedQueryBuilder = () => (
		<div className="space-y-8">
			<div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 dark:from-blue-900/20 dark:to-indigo-900/20 dark:border-blue-800">
				<div className="flex gap-3 items-center mb-2">
					<span className="text-2xl">🎯</span>
					<h4 className="text-sm font-semibold text-blue-900 dark:text-blue-200">
						Visual Query Builder
					</h4>
				</div>
				<p className="text-xs text-blue-800 dark:text-blue-300">
					Build your database query step-by-step with visual controls. No SQL knowledge
					needed!
				</p>
			</div>

			{/* Step 1: Table Selection */}
			<div className="space-y-4">
				<div className="flex gap-2 items-center">
					<div className="flex justify-center items-center w-6 h-6 text-xs font-bold text-white bg-blue-500 rounded-full">
						1
					</div>
					<h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
						Choose Your Table
					</h3>
				</div>

				<div className="pl-8 space-y-3">
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Table Name <span className="text-red-500 dark:text-red-400">*</span>
					</label>
					<div className="relative">
						<input
							type="text"
							value={config.readBuilder?.table || ''}
							onChange={e =>
								debouncedHandleChange({
									readBuilder: {
										...config.readBuilder,
										table: e.target.value,
									},
								})
							}
							placeholder="e.g., users, orders, products"
							className="px-4 py-3 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
						/>
						<div className="flex absolute inset-y-0 right-0 items-center pr-3 pointer-events-none">
							<Table className="w-4 h-4 text-gray-400" />
						</div>
					</div>
					{config.readBuilder?.table && (
						<div className="flex gap-1 items-center text-xs text-green-600 dark:text-green-400">
							<Check className="w-3 h-3" />
							Table selected:{' '}
							<code className="px-1 bg-gray-100 rounded dark:bg-gray-800">
								{config.readBuilder.table}
							</code>
						</div>
					)}
				</div>
			</div>

			{/* Step 2: Filters (Optional) */}
			<div className="space-y-4">
				<div className="flex gap-2 items-center">
					<div className="flex justify-center items-center w-6 h-6 text-xs font-bold text-white bg-gray-400 rounded-full">
						2
					</div>
					<h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
						Add Filters (Optional)
					</h3>
					<span className="text-xs text-gray-500 dark:text-gray-400">
						- Narrow down your results
					</span>
				</div>

				<div className="pl-8 space-y-4">
					<div className="flex justify-between items-center">
						<p className="text-xs text-gray-600 dark:text-gray-400">
							Add conditions to filter your data (e.g., "status = active" or "age{' '}
							{'>'}= 18")
						</p>
						<button
							onClick={() => addFilter()}
							className="flex gap-2 items-center px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg border border-blue-200 transition-colors dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30"
						>
							<Plus className="w-3 h-3" />
							Add Filter
						</button>
					</div>

					{(config.readBuilder?.filters || []).map((filter, index) => (
						<div
							key={index}
							className="flex gap-2 items-end p-3 bg-gray-50 rounded-lg dark:bg-gray-800"
						>
							<div className="grid flex-1 grid-cols-3 gap-3">
								<div className="space-y-1">
									<label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
										Column
									</label>
									<input
										type="text"
										value={filter.column}
										onChange={e =>
											debouncedUpdateFilter(index, { column: e.target.value })
										}
										placeholder="column_name"
										className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
									/>
								</div>
								<div className="space-y-1">
									<label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
										Operator
									</label>
									<select
										value={filter.operator}
										onChange={e =>
											updateFilter(index, { operator: e.target.value as any })
										}
										className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
									>
										<option value="=">Equals (=)</option>
										<option value="!=">Not Equals (!=)</option>
										<option value=">">Greater Than (&gt;)</option>
										<option value="<">Less Than (&lt;)</option>
										<option value=">=">Greater or Equal (&gt;=)</option>
										<option value="<=">Less or Equal (&lt;=)</option>
										<option value="LIKE">Contains (LIKE)</option>
										<option value="IN">In List (IN)</option>
										<option value="IS NULL">Is Empty (NULL)</option>
										<option value="IS NOT NULL">Is Not Empty (NOT NULL)</option>
									</select>
								</div>
								<div className="space-y-1">
									<label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
										Value
									</label>
									{filter.operator === 'IS NULL' ||
									filter.operator === 'IS NOT NULL' ? (
										<div className="px-2 py-1.5 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 rounded">
											No value needed
										</div>
									) : (
										<VariablePicker
											nodeId={nodeId}
											value={filter.value || ''}
											onChange={value => updateFilter(index, { value })}
											placeholder="{{token}} or value"
											className="w-full text-sm"
										/>
									)}
								</div>
							</div>
							<button
								onClick={() => removeFilter(index)}
								className="p-1 text-red-500 rounded dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
							>
								<X className="w-4 h-4" />
							</button>
						</div>
					))}

					{(!config.readBuilder?.filters || config.readBuilder.filters.length === 0) && (
						<div className="py-6 text-center text-gray-500 rounded-lg border-2 border-gray-300 border-dashed dark:text-gray-400 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/50">
							<div className="mb-2 text-2xl text-gray-400 dark:text-gray-500">🔍</div>
							<p className="mb-1 text-sm font-medium">No filters added yet</p>
							<p className="text-xs">
								Your query will return all rows from the table
							</p>
							<button
								onClick={() => addFilter()}
								className="mt-3 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-md border border-blue-200 transition-colors dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30"
							>
								Add your first filter
							</button>
						</div>
					)}
				</div>
			</div>

			{/* Step 3: Order By (Optional) */}
			<div className="space-y-4">
				<div className="flex gap-2 items-center">
					<div className="flex justify-center items-center w-6 h-6 text-xs font-bold text-white bg-gray-400 rounded-full">
						3
					</div>
					<h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
						Sort Results (Optional)
					</h3>
					<span className="text-xs text-gray-500 dark:text-gray-400">
						- Control the order of your data
					</span>
				</div>

				<div className="pl-8 space-y-4">
					<div className="flex justify-between items-center">
						<p className="text-xs text-gray-600 dark:text-gray-400">
							Choose how to sort your results (e.g., by date, name, or ID)
						</p>
						<button
							onClick={() => addOrderBy()}
							className="flex gap-2 items-center px-3 py-2 text-xs font-medium text-purple-600 bg-purple-50 rounded-lg border border-purple-200 transition-colors dark:text-purple-400 dark:bg-purple-900/20 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/30"
						>
							<Plus className="w-3 h-3" />
							Add Sort
						</button>
					</div>

					{(config.readBuilder?.orderBy || []).map((order, index) => (
						<div
							key={index}
							className="flex gap-2 items-end p-3 bg-gray-50 rounded-lg dark:bg-gray-800"
						>
							<div className="grid flex-1 grid-cols-2 gap-3">
								<div className="space-y-1">
									<label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
										Column
									</label>
									<input
										type="text"
										value={order.column}
										onChange={e =>
											updateOrderBy(index, { column: e.target.value })
										}
										placeholder="column_name"
										className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
									/>
								</div>
								<div className="space-y-1">
									<label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
										Direction
									</label>
									<select
										value={order.direction}
										onChange={e =>
											updateOrderBy(index, {
												direction: e.target.value as 'asc' | 'desc',
											})
										}
										className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
									>
										<option value="asc">Ascending (A-Z, 1-9)</option>
										<option value="desc">Descending (Z-A, 9-1)</option>
									</select>
								</div>
							</div>
							<button
								onClick={() => removeOrderBy(index)}
								className="p-1 text-red-500 rounded dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
							>
								<X className="w-4 h-4" />
							</button>
						</div>
					))}

					{(!config.readBuilder?.orderBy || config.readBuilder.orderBy.length === 0) && (
						<div className="py-6 text-center text-gray-500 rounded-lg border-2 border-gray-300 border-dashed dark:text-gray-400 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/50">
							<div className="mb-2 text-2xl text-gray-400 dark:text-gray-500">🔢</div>
							<p className="mb-1 text-sm font-medium">No sorting added yet</p>
							<p className="text-xs">
								Results will appear in database order (usually by ID)
							</p>
							<button
								onClick={() => addOrderBy()}
								className="mt-3 px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 rounded-md border border-purple-200 transition-colors dark:text-purple-400 dark:bg-purple-900/20 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/30"
							>
								Add sorting
							</button>
						</div>
					)}
				</div>
			</div>

			{/* Step 4: Limit Results */}
			<div className="space-y-4">
				<div className="flex gap-2 items-center">
					<div className="flex justify-center items-center w-6 h-6 text-xs font-bold text-white bg-gray-400 rounded-full">
						4
					</div>
					<h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
						Limit Results
					</h3>
					<span className="text-xs text-gray-500 dark:text-gray-400">
						- Control how many rows to return
					</span>
				</div>

				<div className="grid grid-cols-2 gap-6 pl-8">
					<div className="space-y-3">
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
							Max Rows to Return
						</label>
						<div className="relative">
							<input
								type="number"
								value={config.readBuilder?.limit || 100}
								onChange={e =>
									handleChange({
										readBuilder: {
											...config.readBuilder,
											limit: parseInt(e.target.value) || 100,
										},
									})
								}
								min="1"
								max="10000"
								placeholder="100"
								className="px-4 py-3 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
							/>
							<div className="flex absolute inset-y-0 right-0 items-center pr-3 pointer-events-none">
								<span className="text-xs text-gray-400">rows</span>
							</div>
						</div>
						<p className="text-xs text-gray-500 dark:text-gray-400">
							Recommended: 100-1000 for performance
						</p>
					</div>
					<div className="space-y-3">
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
							Skip Rows (Advanced)
						</label>
						<div className="relative">
							<input
								type="number"
								value={config.readBuilder?.offset || 0}
								onChange={e =>
									handleChange({
										readBuilder: {
											...config.readBuilder,
											offset: parseInt(e.target.value) || 0,
										},
									})
								}
								min="0"
								placeholder="0"
								className="px-4 py-3 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
							/>
							<div className="flex absolute inset-y-0 right-0 items-center pr-3 pointer-events-none">
								<span className="text-xs text-gray-400">skip</span>
							</div>
						</div>
						<p className="text-xs text-gray-500 dark:text-gray-400">
							For pagination (usually 0)
						</p>
					</div>
				</div>
			</div>

			{/* Generated SQL Preview */}
			<div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200 dark:from-gray-800 dark:to-gray-900 dark:border-gray-700">
				<div className="flex gap-2 items-center mb-3">
					<span className="text-lg">⚡</span>
					<h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
						Generated SQL Query
					</h5>
					<button
						onClick={() => navigator.clipboard.writeText(generateSqlPreview())}
						className="px-2 py-1 ml-auto text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
						title="Copy SQL to clipboard"
					>
						📋 Copy
					</button>
				</div>
				<div className="relative">
					<code className="block p-3 font-mono text-sm text-gray-800 whitespace-pre-wrap bg-white rounded-lg border shadow-sm dark:text-gray-200 dark:bg-gray-900 dark:border-gray-600">
						{generateSqlPreview()}
					</code>
				</div>
				<p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
					This is the SQL query that will be executed based on your configuration above.
				</p>
			</div>
		</div>
	);

	const renderAdvancedSqlEditor = () => (
		<div className="space-y-6">
			{/* Query Templates */}
			<div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800">
				<h4 className="mb-2 text-sm font-medium text-yellow-900 dark:text-yellow-200">
					Quick Templates
				</h4>
				<div className="grid grid-cols-2 gap-2">
					{QUERY_TEMPLATES.map(template => (
						<button
							key={template.name}
							onClick={() => loadTemplate(template)}
							className="flex gap-2 items-start p-2 text-xs text-left text-yellow-800 bg-white rounded border border-yellow-300 transition-colors dark:text-yellow-300 dark:bg-gray-700 dark:border-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20"
						>
							<Zap className="w-3 h-3 mt-0.5 flex-shrink-0" />
							<div>
								<div className="font-medium">{template.name}</div>
								<div className="text-yellow-700 dark:text-yellow-400">
									{template.description}
								</div>
							</div>
						</button>
					))}
				</div>
			</div>

			{/* SQL Query Editor */}
			<div className="space-y-2">
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
					SQL Query <span className="text-red-500 dark:text-red-400">*</span>
				</label>
				<div className="overflow-hidden rounded-lg border">
					<Editor
						height="200px"
						defaultLanguage="sql"
						value={config.query || ''}
						onChange={value => debouncedHandleChange({ query: value || '' })}
						options={{
							minimap: { enabled: false },
							lineNumbers: 'on',
							scrollBeyondLastLine: false,
							wordWrap: 'on',
							wrappingIndent: 'indent',
							automaticLayout: true,
							formatOnPaste: true,
						}}
						theme={isDark ? 'vs-dark' : 'vs-light'}
					/>
				</div>
				<div className="text-xs text-gray-500 dark:text-gray-400">
					Use :parameterName for parameters and {'{{tokenName}}'} for dynamic values
				</div>
			</div>

			{/* Parameters */}
			<div className="space-y-4">
				<div className="flex justify-between items-center">
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Query Parameters
					</label>
					<button
						onClick={addParameter}
						className="flex gap-1 items-center px-2 py-1 text-xs text-green-600 rounded dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20"
					>
						<Plus className="w-3 h-3" />
						Add Parameter
					</button>
				</div>

				{Object.entries(config.parameters || {}).map(([paramName, tokenValue]) => (
					<div
						key={paramName}
						className="flex gap-2 items-center p-3 bg-gray-50 rounded-lg dark:bg-gray-800"
					>
						<div className="grid flex-1 grid-cols-2 gap-3">
							<div className="space-y-1">
								<label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
									Parameter Name
								</label>
								<input
									type="text"
									value={paramName}
									onChange={e =>
										updateParameter(paramName, e.target.value, tokenValue)
									}
									placeholder="paramName"
									className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
								/>
							</div>
							<div className="space-y-1">
								<label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
									Token Value
								</label>
								<VariablePicker
									nodeId={nodeId}
									value={tokenValue}
									onChange={value => updateParameter(paramName, paramName, value)}
									placeholder="{{fieldName}} or static value"
									className="w-full text-sm"
								/>
							</div>
						</div>
						<button
							onClick={() => removeParameter(paramName)}
							className="p-1 text-red-500 rounded dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
						>
							<X className="w-4 h-4" />
						</button>
					</div>
				))}

				{(!config.parameters || Object.keys(config.parameters).length === 0) && (
					<div className="py-4 text-center text-gray-500 rounded-lg border-2 border-gray-300 border-dashed dark:text-gray-400 dark:border-gray-600">
						<Table className="mx-auto mb-1 w-5 h-5 text-gray-400 dark:text-gray-500" />
						<p className="text-xs">No parameters configured</p>
					</div>
				)}
			</div>

			{/* Query Validation Status */}
			{queryError && (
				<div className="p-3 mt-3 bg-red-50 rounded-lg border border-red-200 dark:bg-red-900/20 dark:border-red-800">
					<div className="text-sm font-medium text-red-700 dark:text-red-200">
						Query Error:
					</div>
					<div className="mt-1 text-sm text-red-600 dark:text-red-300">{queryError}</div>
				</div>
			)}
		</div>
	);

	// Insert configuration
	const renderInsertConfiguration = () => (
		<div className="space-y-6">
			<div className="p-3 bg-green-50 rounded-lg border border-green-200 dark:bg-green-900/20 dark:border-green-800">
				<h4 className="mb-2 text-sm font-medium text-green-900 dark:text-green-200">
					Insert Rows
				</h4>
				<p className="text-xs text-green-800 dark:text-green-300">
					Configure data insertion with dynamic values from upstream nodes
				</p>
			</div>

			{/* Target Table */}
			<div className="space-y-2">
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
					Target Table <span className="text-red-500 dark:text-red-400">*</span>
				</label>
				<input
					type="text"
					value={config.insertConfig?.table || ''}
					onChange={e =>
						debouncedHandleChange({
							insertConfig: {
								...config.insertConfig,
								table: e.target.value,
							},
						})
					}
					placeholder="users"
					className="px-3 py-2 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
				/>
			</div>

			{/* Row Mapping */}
			<div className="space-y-4">
				<div className="flex justify-between items-center">
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Column Mapping
					</label>
					<button
						onClick={() => addColumnMapping()}
						className="flex gap-1 items-center px-2 py-1 text-xs text-green-600 rounded dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20"
					>
						<Plus className="w-3 h-3" />
						Add Column
					</button>
				</div>

				{Object.entries(config.insertConfig?.rows?.[0] || {}).map(
					([column, value], index) => (
						<div
							key={index}
							className="flex gap-2 items-end p-3 bg-gray-50 rounded-lg dark:bg-gray-800"
						>
							<div className="grid flex-1 grid-cols-2 gap-3">
								<div className="space-y-1">
									<label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
										Column Name
									</label>
									<input
										type="text"
										value={column}
										onChange={e => updateColumnName(column, e.target.value)}
										placeholder="column_name"
										className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
									/>
								</div>
								<div className="space-y-1">
									<label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
										Value Source
									</label>
									<VariablePicker
										nodeId={nodeId}
										value={value}
										onChange={newValue => updateColumnValue(column, newValue)}
										placeholder="{{token}} or static value"
										className="w-full text-sm"
									/>
								</div>
							</div>
							<button
								onClick={() => removeColumnMapping(column)}
								className="p-1 text-red-500 rounded dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
							>
								<X className="w-4 h-4" />
							</button>
						</div>
					)
				)}

				{(!config.insertConfig?.rows?.[0] ||
					Object.keys(config.insertConfig.rows[0]).length === 0) && (
					<div className="py-4 text-center text-gray-500 rounded-lg border-2 border-gray-300 border-dashed dark:text-gray-400 dark:border-gray-600">
						<div className="mb-1 text-gray-400 dark:text-gray-500">📝</div>
						<p className="text-xs">No columns mapped - add columns to insert data</p>
					</div>
				)}
			</div>

			{/* Batch Insert Option */}
			<div className="space-y-3">
				<div className="flex gap-2 items-center">
					<input
						type="checkbox"
						id="batchInsert"
						checked={!!config.insertConfig?.batchToken}
						onChange={e =>
							handleChange({
								insertConfig: {
									...config.insertConfig,
									batchToken: e.target.checked ? '' : undefined,
								},
							})
						}
						className="w-4 h-4 text-green-600 bg-white rounded border-gray-300 dark:text-green-400 dark:border-gray-600 focus:ring-green-500 dark:focus:ring-green-400 dark:bg-gray-700"
					/>
					<label
						htmlFor="batchInsert"
						className="text-sm text-gray-700 dark:text-gray-300"
					>
						Batch Insert (insert multiple rows from array)
					</label>
				</div>

				{config.insertConfig?.batchToken !== undefined && (
					<div className="space-y-2">
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
							Array Token <span className="text-red-500 dark:text-red-400">*</span>
						</label>
						<VariablePicker
							nodeId={nodeId}
							value={config.insertConfig?.batchToken || ''}
							onChange={value =>
								handleChange({
									insertConfig: {
										...config.insertConfig,
										batchToken: value,
									},
								})
							}
							placeholder="{{arrayField}} e.g. {{cartItems}}"
							className="w-full text-sm"
						/>
						<div className="text-xs text-gray-500 dark:text-gray-400">
							Select an array field from upstream nodes. Each item will create a row.
						</div>
					</div>
				)}
			</div>

			{/* Conflict Handling */}
			<div className="space-y-3">
				<div className="flex gap-2 items-center">
					<input
						type="checkbox"
						id="conflictHandling"
						checked={!!config.insertConfig?.conflictHandling?.enabled}
						onChange={e =>
							handleChange({
								insertConfig: {
									...config.insertConfig,
									conflictHandling: {
										...config.insertConfig?.conflictHandling,
										enabled: e.target.checked,
									},
								},
							})
						}
						className="w-4 h-4 text-orange-600 bg-white rounded border-gray-300 dark:text-orange-400 dark:border-gray-600 focus:ring-orange-500 dark:focus:ring-orange-400 dark:bg-gray-700"
					/>
					<label
						htmlFor="conflictHandling"
						className="text-sm text-gray-700 dark:text-gray-300"
					>
						Handle Conflicts (Upsert Mode)
					</label>
				</div>

				{config.insertConfig?.conflictHandling?.enabled && (
					<div className="p-3 space-y-4 bg-orange-50 rounded-lg border border-orange-200 dark:bg-orange-900/20 dark:border-orange-800">
						<div className="mb-3 text-xs text-orange-800 dark:text-orange-300">
							Configure how to handle rows that already exist (based on unique
							constraints)
						</div>

						<div className="space-y-2">
							<label className="block text-xs font-medium text-orange-900 dark:text-orange-200">
								Conflict Columns (Primary Key / Unique)
							</label>
							<input
								type="text"
								value={
									config.insertConfig?.conflictHandling?.conflictColumns?.join(
										', '
									) || ''
								}
								onChange={e =>
									handleChange({
										insertConfig: {
											...config.insertConfig,
											conflictHandling: {
												...config.insertConfig?.conflictHandling,
												conflictColumns: e.target.value
													.split(',')
													.map(s => s.trim())
													.filter(Boolean),
											},
										},
									})
								}
								placeholder="id, email"
								className="w-full px-2 py-1.5 text-sm border border-orange-300 dark:border-orange-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400"
							/>
						</div>

						<div className="space-y-2">
							<label className="block text-xs font-medium text-orange-900 dark:text-orange-200">
								Update Columns (on conflict)
							</label>
							<input
								type="text"
								value={
									config.insertConfig?.conflictHandling?.updateColumns?.join(
										', '
									) || ''
								}
								onChange={e =>
									handleChange({
										insertConfig: {
											...config.insertConfig,
											conflictHandling: {
												...config.insertConfig?.conflictHandling,
												updateColumns: e.target.value
													.split(',')
													.map(s => s.trim())
													.filter(Boolean),
											},
										},
									})
								}
								placeholder="name, updated_at"
								className="w-full px-2 py-1.5 text-sm border border-orange-300 dark:border-orange-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400"
							/>
						</div>
					</div>
				)}
			</div>
		</div>
	);

	const renderCreateTableConfiguration = () => (
		<div className="space-y-6">
			<div className="p-3 bg-purple-50 rounded-lg border border-purple-200 dark:bg-purple-900/20 dark:border-purple-800">
				<h4 className="mb-2 text-sm font-medium text-purple-900 dark:text-purple-200">
					Create Table
				</h4>
				<p className="text-xs text-purple-800 dark:text-purple-300">
					Design a new table structure with columns, types, and constraints
				</p>
			</div>

			{/* Table Name */}
			<div className="space-y-2">
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
					Table Name <span className="text-red-500 dark:text-red-400">*</span>
				</label>
				<input
					type="text"
					value={config.createTableConfig?.table || ''}
					onChange={e =>
						debouncedHandleChange({
							createTableConfig: {
								...config.createTableConfig,
								table: e.target.value,
							},
						})
					}
					placeholder="my_new_table"
					className="px-3 py-2 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
				/>
			</div>

			{/* IF NOT EXISTS Option */}
			<div className="flex gap-2 items-center">
				<input
					type="checkbox"
					id="ifNotExists"
					checked={config.createTableConfig?.ifNotExists !== false}
					onChange={e =>
						handleChange({
							createTableConfig: {
								...config.createTableConfig,
								ifNotExists: e.target.checked,
							},
						})
					}
					className="w-4 h-4 text-purple-600 bg-white rounded border-gray-300 dark:text-purple-400 dark:border-gray-600 focus:ring-purple-500 dark:focus:ring-purple-400 dark:bg-gray-700"
				/>
				<label htmlFor="ifNotExists" className="text-sm text-gray-700 dark:text-gray-300">
					Create only if table doesn't exist (recommended)
				</label>
			</div>

			{/* Schema Alteration Options */}
			<div className="p-4 space-y-4 bg-amber-50 rounded-lg border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
				<div className="flex gap-2 items-start">
					<input
						type="checkbox"
						id="allowSchemaAlteration"
						checked={config.createTableConfig?.allowSchemaAlteration || false}
						onChange={e =>
							handleChange({
								createTableConfig: {
									...config.createTableConfig,
									allowSchemaAlteration: e.target.checked,
								},
							})
						}
						className="w-4 h-4 mt-0.5 text-amber-600 bg-white rounded border-gray-300 dark:text-amber-400 dark:border-gray-600 focus:ring-amber-500 dark:focus:ring-amber-400 dark:bg-gray-700"
					/>
					<div className="space-y-1">
						<label
							htmlFor="allowSchemaAlteration"
							className="text-sm font-medium text-amber-900 dark:text-amber-200"
						>
							Allow Schema Alteration
						</label>
						<p className="text-xs text-amber-800 dark:text-amber-300">
							Enable modification of existing table schemas. This will compare the
							current table structure with your configuration and apply necessary
							changes.
						</p>
					</div>
				</div>

				{config.createTableConfig?.allowSchemaAlteration && (
					<div className="pl-6 space-y-4 border-l-2 border-amber-300 dark:border-amber-700">
						{/* Warning about schema changes */}
						<div className="p-3 bg-amber-100 rounded-lg border border-amber-300 dark:bg-amber-900/30 dark:border-amber-700">
							<div className="flex gap-2 items-start">
								<span className="text-sm text-amber-600 dark:text-amber-400">
									⚠️
								</span>
								<div className="space-y-1">
									<p className="text-xs font-medium text-amber-900 dark:text-amber-200">
										Schema Alteration Enabled
									</p>
									<p className="text-xs text-amber-800 dark:text-amber-300">
										This will modify existing tables if the schema differs from
										your configuration. Data loss may occur when removing
										columns or changing incompatible types.
									</p>
								</div>
							</div>
						</div>

						{/* Validation Mode */}
						<div className="space-y-2">
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
								Validation Mode
							</label>
							<select
								value={config.createTableConfig?.schemaValidationMode || 'STRICT'}
								onChange={e =>
									handleChange({
										createTableConfig: {
											...config.createTableConfig,
											schemaValidationMode: e.target.value as
												| 'STRICT'
												| 'PERMISSIVE',
										},
									})
								}
								className="px-3 py-2 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-500 dark:focus:ring-amber-400"
							>
								<option value="STRICT">Strict - Block risky operations</option>
								<option value="PERMISSIVE">Permissive - Allow with warnings</option>
							</select>
							<p className="text-xs text-gray-600 dark:text-gray-400">
								Strict mode blocks potentially dangerous operations, while
								permissive mode allows them with explicit confirmation.
							</p>
						</div>

						{/* Dry Run Option */}
						<div className="flex gap-2 items-start">
							<input
								type="checkbox"
								id="dryRun"
								checked={config.createTableConfig?.dryRun || false}
								onChange={e =>
									handleChange({
										createTableConfig: {
											...config.createTableConfig,
											dryRun: e.target.checked,
										},
									})
								}
								className="w-4 h-4 mt-0.5 text-amber-600 bg-white rounded border-gray-300 dark:text-amber-400 dark:border-gray-600 focus:ring-amber-500 dark:focus:ring-amber-400 dark:bg-gray-700"
							/>
							<div className="space-y-1">
								<label
									htmlFor="dryRun"
									className="text-sm font-medium text-gray-700 dark:text-gray-300"
								>
									Dry Run (Preview Changes)
								</label>
								<p className="text-xs text-gray-600 dark:text-gray-400">
									Preview schema changes without executing them. Shows what would
									be changed and potential risks.
								</p>
							</div>
						</div>

						{/* Schema Validation Button */}
						<div className="pt-2">
							<button
								onClick={() => handleSchemaValidation()}
								disabled={!config.createTableConfig?.table || isValidatingSchema}
								className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-amber-700 bg-amber-100 rounded-lg border border-amber-300 transition-colors dark:text-amber-300 dark:bg-amber-900/30 dark:border-amber-700 hover:bg-amber-200 dark:hover:bg-amber-900/50 disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{isValidatingSchema ? (
									<>
										<div className="w-4 h-4 rounded-full border-2 border-amber-600 animate-spin border-t-transparent dark:border-amber-400"></div>
										Validating...
									</>
								) : (
									<>
										<span className="text-amber-600 dark:text-amber-400">
											🔍
										</span>
										Check Schema Changes
									</>
								)}
							</button>
							<p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
								Validate your schema changes and see potential impacts before
								execution
							</p>
						</div>
					</div>
				)}
			</div>

			{/* Design Mode Toggle */}
			<div className="space-y-4">
				<div className="flex gap-2 items-center justify-between">
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Table Design Mode
					</label>
					<div className="flex p-1 bg-gray-100 rounded-lg dark:bg-gray-700">
						<button
							onClick={() => setTableDesignMode('manual')}
							className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
								tableDesignMode === 'manual'
									? 'bg-white text-gray-900 shadow-sm dark:bg-gray-600 dark:text-gray-100'
									: 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
							}`}
						>
							Manual
						</button>
						<button
							onClick={() => setTableDesignMode('sql')}
							className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
								tableDesignMode === 'sql'
									? 'bg-white text-gray-900 shadow-sm dark:bg-gray-600 dark:text-gray-100'
									: 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
							}`}
						>
							Paste SQL
						</button>
					</div>
				</div>

				{tableDesignMode === 'sql' ? renderSqlPasteMode() : renderManualColumnsDesigner()}
			</div>

			{/* Generated SQL Preview */}
			<div className="p-3 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
				<h5 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
					Generated SQL Preview
				</h5>
				<code className="block p-2 font-mono text-xs text-gray-600 whitespace-pre-wrap bg-white rounded border dark:text-gray-400 dark:bg-gray-900">
					{generateCreateTablePreview()}
				</code>
			</div>
		</div>
	);

	// Table column management functions
	const addTableColumn = () => {
		const existingColumns = config.createTableConfig?.columns || [];
		const hasIdColumn = existingColumns.some(col => col.name === 'id');

		const newColumn = {
			name: '',
			// Always use integer for ID columns (consistent with PostgreSQL reality)
			type: hasIdColumn ? 'varchar(255)' : 'integer',
			nullable: !hasIdColumn, // Make first column (usually ID) non-nullable
			primaryKey: !hasIdColumn, // Make first column primary key if no ID exists
		};

		handleChange({
			createTableConfig: {
				...config.createTableConfig,
				columns: [...(config.createTableConfig?.columns || []), newColumn],
			},
		});
	};

	const updateTableColumn = useCallback(
		(index: number, updates: Partial<any>) => {
			const columns = [...(config.createTableConfig?.columns || [])];
			columns[index] = { ...columns[index], ...updates };

			handleChange({
				createTableConfig: {
					...config.createTableConfig,
					columns,
				},
			});
		},
		[config.createTableConfig, handleChange]
	);

	// Debounced version for text inputs
	const debouncedUpdateTableColumn = useDebouncedCallback(
		(index: number, updates: Partial<any>) => {
			const columns = [...(config.createTableConfig?.columns || [])];
			columns[index] = { ...columns[index], ...updates };

			setConfig(currentConfig => {
				const updatedConfig = {
					...currentConfig,
					createTableConfig: {
						...currentConfig.createTableConfig,
						columns,
					},
				};
				onChange({
					...data,
					config: updatedConfig,
				});
				return updatedConfig;
			});
		},
		300
	);

	// Cleanup effect for all debounced functions
	useEffect(() => {
		return () => {
			// Cancel any pending debounced calls on unmount
			debouncedHandleChange.cancel();
			debouncedUpdateFilter.cancel();
			debouncedUpdateTableColumn.cancel();
		};
	}, [debouncedHandleChange, debouncedUpdateFilter, debouncedUpdateTableColumn]);

	const removeTableColumn = (index: number) => {
		const columns = [...(config.createTableConfig?.columns || [])];
		columns.splice(index, 1);

		handleChange({
			createTableConfig: {
				...config.createTableConfig,
				columns,
			},
		});
	};

	// Generate CREATE TABLE SQL preview
	const generateCreateTablePreview = (): string => {
		if (!config.createTableConfig?.table) {
			return 'CREATE TABLE [table_name] (\n  [column definitions]\n)';
		}

		const tableName = config.createTableConfig.table;
		const columns = config.createTableConfig.columns || [];

		if (columns.length === 0) {
			return `CREATE TABLE ${
				config.createTableConfig.ifNotExists !== false ? 'IF NOT EXISTS ' : ''
			}${tableName} (\n  [no columns defined]\n)`;
		}

		const columnDefs = columns.map(col => {
			let def = `  ${col.name} ${col.type}`;
			if (!col.nullable) def += ' NOT NULL';
			if (col.default) def += ` DEFAULT ${col.default}`;
			return def;
		});

		// Add primary key constraint
		const primaryKeys = columns.filter(col => col.primaryKey).map(col => col.name);
		if (primaryKeys.length > 0) {
			columnDefs.push(`  PRIMARY KEY (${primaryKeys.join(', ')})`);
		}

		const ifNotExists = config.createTableConfig.ifNotExists !== false ? 'IF NOT EXISTS ' : '';
		return `CREATE TABLE ${ifNotExists}${tableName} (\n${columnDefs.join(',\n')}\n)`;
	};

	// Function to parse SQL and populate columns
	const handleSqlParse = async () => {
		if (!sqlInput.trim()) {
			setSqlParseError('Please enter a CREATE TABLE statement');
			return;
		}

		setIsParsingSql(true);
		setSqlParseError(null);

		try {
			const response = await fetch('/api/v1/sql/parse-create-table', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
				},
				body: JSON.stringify({ sql: sqlInput }),
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(errorData.message || 'Failed to parse SQL');
			}

			const result = await response.json();

			// Update the table configuration with parsed data
			handleChange({
				createTableConfig: {
					...config.createTableConfig,
					table: result.tableName,
					columns: result.columns,
					indexes: result.indexes || [],
					ifNotExists: result.ifNotExists || false,
				},
			});

			// Switch back to manual mode to allow editing
			setTableDesignMode('manual');
			setSqlInput(''); // Clear the SQL input
		} catch (error) {
			setSqlParseError(error instanceof Error ? error.message : 'Failed to parse SQL');
		} finally {
			setIsParsingSql(false);
		}
	};

	// Render SQL paste mode
	const renderSqlPasteMode = () => (
		<div className="space-y-4">
			<div className="p-3 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
				<h5 className="mb-2 text-sm font-medium text-blue-900 dark:text-blue-200">
					Paste CREATE TABLE SQL
				</h5>
				<p className="text-xs text-blue-800 dark:text-blue-300">
					Paste your CREATE TABLE statement below and we'll automatically extract the
					table schema for you.
				</p>
			</div>

			<div className="space-y-3">
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
					SQL Statement
				</label>
				<textarea
					value={sqlInput}
					onChange={e => setSqlInput(e.target.value)}
					placeholder={`CREATE TABLE users (
  id SERIAL PRIMARY KEY,
  name VARCHAR(255) NOT NULL,
  email VARCHAR(255) UNIQUE,
  created_at TIMESTAMP DEFAULT NOW()
);`}
					rows={8}
					className="w-full px-3 py-2 text-sm font-mono text-gray-900 bg-white rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
				/>

				{sqlParseError && (
					<div className="p-3 bg-red-50 rounded-lg border border-red-200 dark:bg-red-900/20 dark:border-red-800">
						<div className="text-sm font-medium text-red-700 dark:text-red-200">
							Parse Error:
						</div>
						<div className="mt-1 text-sm text-red-600 dark:text-red-300">
							{sqlParseError}
						</div>
					</div>
				)}

				<div className="flex gap-3">
					<button
						onClick={handleSqlParse}
						disabled={isParsingSql || !sqlInput.trim()}
						className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg border border-transparent transition-colors hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						{isParsingSql ? (
							<>
								<div className="w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent"></div>
								Parsing...
							</>
						) : (
							<>
								<span>🔍</span>
								Parse SQL
							</>
						)}
					</button>
					<button
						onClick={() => {
							setSqlInput('');
							setSqlParseError(null);
							setTableDesignMode('manual');
						}}
						className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600"
					>
						Cancel
					</button>
				</div>
			</div>
		</div>
	);

	// Render manual columns designer
	const renderManualColumnsDesigner = () => (
		<div className="space-y-4">
			<div className="flex justify-between items-center">
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
					Columns
				</label>
				<button
					onClick={() => addTableColumn()}
					className="flex gap-1 items-center px-2 py-1 text-xs text-purple-600 rounded dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20"
				>
					<Plus className="w-3 h-3" />
					Add Column
				</button>
			</div>

			{(config.createTableConfig?.columns || []).map((column, index) => (
				<div
					key={index}
					className="p-4 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700"
				>
					<div className="grid grid-cols-2 gap-4 mb-3">
						<div className="space-y-1">
							<label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
								Column Name{' '}
								<span className="text-red-500 dark:text-red-400">*</span>
							</label>
							<input
								type="text"
								value={column.name}
								onChange={e =>
									debouncedUpdateTableColumn(index, { name: e.target.value })
								}
								placeholder="column_name"
								className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
							/>
						</div>
						<div className="space-y-1">
							<label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
								Data Type <span className="text-red-500 dark:text-red-400">*</span>
							</label>
							<select
								value={column.type}
								onChange={e => updateTableColumn(index, { type: e.target.value })}
								className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
							>
								<option value="integer">🔢 Auto-Increment ID (Integer)</option>
								<option value="uuid">🆔 UUID (Unique Identifier)</option>
								<option value="bigint">Big Integer</option>
								<option value="varchar(255)">Varchar(255)</option>
								<option value="text">Text</option>
								<option value="numeric(10,2)">Decimal(10,2)</option>
								<option value="boolean">Boolean</option>
								<option value="timestamp">Timestamp</option>
								<option value="timestamptz">Timestamp with Timezone</option>
								<option value="date">Date</option>
								<option value="json">JSON</option>
								<option value="jsonb">JSONB (Binary JSON)</option>
							</select>
						</div>
					</div>

					<div className="flex gap-4 items-center mb-3">
						<label className="flex gap-2 items-center text-sm">
							<input
								type="checkbox"
								checked={!column.nullable}
								onChange={e =>
									updateTableColumn(index, { nullable: !e.target.checked })
								}
								className="w-4 h-4 text-purple-600 bg-white rounded border-gray-300 dark:text-purple-400 dark:border-gray-600 focus:ring-purple-500 dark:focus:ring-purple-400 dark:bg-gray-700"
							/>
							<span className="text-gray-700 dark:text-gray-300">
								Required (NOT NULL)
							</span>
						</label>

						<div className="space-y-1">
							<label className="flex gap-2 items-center text-sm">
								<input
									type="checkbox"
									checked={!!column.primaryKey}
									onChange={e =>
										updateTableColumn(index, {
											primaryKey: e.target.checked,
										})
									}
									className="w-4 h-4 text-purple-600 bg-white rounded border-gray-300 dark:text-purple-400 dark:border-gray-600 focus:ring-purple-500 dark:focus:ring-purple-400 dark:bg-gray-700"
								/>
								<span className="text-gray-700 dark:text-gray-300">
									Primary Key
								</span>
							</label>
							{config.createTableConfig?.allowSchemaAlteration &&
								column.primaryKey && (
									<p className="pl-6 text-xs text-amber-600 dark:text-amber-400">
										🔒 Primary key columns are protected from modification
										during schema alteration
									</p>
								)}
						</div>
					</div>

					<div className="flex gap-2 items-end">
						<div className="flex-1 space-y-1">
							<label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
								Default Value (optional)
							</label>
							<input
								type="text"
								value={column.default || ''}
								onChange={e =>
									debouncedUpdateTableColumn(index, {
										default: e.target.value || undefined,
									})
								}
								placeholder="NULL, NOW(), 'default_value'"
								className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
							/>
						</div>
						<button
							onClick={() => removeTableColumn(index)}
							className="p-1.5 text-red-500 rounded dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
						>
							<X className="w-4 h-4" />
						</button>
					</div>
				</div>
			))}

			{(!config.createTableConfig?.columns ||
				config.createTableConfig.columns.length === 0) && (
				<div className="py-4 text-center text-gray-500 rounded-lg border-2 border-gray-300 border-dashed dark:text-gray-400 dark:border-gray-600">
					<div className="mb-1 text-gray-400 dark:text-gray-500">🏗️</div>
					<p className="text-xs">No columns defined - add columns to create table</p>
				</div>
			)}
		</div>
	);

	const renderUpsertConfiguration = () => (
		<div className="p-4 bg-orange-50 rounded-lg border border-orange-200 dark:bg-orange-900/20 dark:border-orange-800">
			<h4 className="mb-2 text-sm font-medium text-orange-900 dark:text-orange-200">
				Upsert Configuration
			</h4>
			<p className="text-sm text-orange-800 dark:text-orange-300">
				🚧 Upsert configuration coming soon!
			</p>
		</div>
	);

	const renderTestTab = () => (
		<div className="space-y-6">
			<div>
				<h4 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
					Query Test & Preview
				</h4>
				<p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
					Run your query with sample data to preview results and validate functionality
				</p>
			</div>

			{/* Run Query */}
			<div className="space-y-3">
				<button
					onClick={runQuery}
					disabled={isRunningQuery || !config.query?.trim()}
					className="flex gap-2 items-center px-3 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-md transition-colors dark:text-teal-300 dark:bg-teal-900/20 dark:border-teal-800 hover:bg-teal-100 dark:hover:bg-teal-900/30 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-500 disabled:cursor-not-allowed"
				>
					<Play className="w-3 h-3" />
					{isRunningQuery ? 'Running Query...' : 'Run Query'}
				</button>

				{/* Query Info */}
				<div className="p-3 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
					<h5 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
						Query Details
					</h5>
					<div className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
						<div>
							<strong>Query:</strong> {(config.query || '').split('\n')[0]}...
						</div>
						<div>
							<strong>Parameters:</strong>{' '}
							{Object.keys(config.parameters || {}).length} parameters
						</div>
						<div>
							<strong>Resolved Values:</strong>
						</div>
						<div className="ml-4 space-y-1">
							{Object.entries(config.parameters || {}).map(([param, token]) => (
								<div key={param}>
									<code className="px-1 bg-gray-100 rounded dark:bg-gray-700">
										{param}
									</code>{' '}
									={' '}
									<code className="px-1 bg-gray-100 rounded dark:bg-gray-700">
										{evaluateExpression(token, enhancedMockContext)}
									</code>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>

			{/* Query Results */}
			{queryResult && (
				<div className="space-y-4">
					<div className="flex justify-between items-center">
						<h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">
							Query Results
						</h5>
						<div className="flex gap-4 items-center">
							{/* View Mode Toggle */}
							<div className="flex p-1 bg-gray-100 rounded-lg dark:bg-gray-700">
								<button
									onClick={() => setResultViewMode('table')}
									className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
										resultViewMode === 'table'
											? 'bg-white text-gray-900 shadow-sm dark:bg-gray-600 dark:text-gray-100'
											: 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
									}`}
								>
									Table
								</button>
								<button
									onClick={() => setResultViewMode('json')}
									className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
										resultViewMode === 'json'
											? 'bg-white text-gray-900 shadow-sm dark:bg-gray-600 dark:text-gray-100'
											: 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
									}`}
								>
									JSON
								</button>
							</div>
							<div className="text-xs text-gray-500 dark:text-gray-400">
								{queryResult.rowCount} rows • {queryResult.executionTime.toFixed(1)}
								ms
							</div>
						</div>
					</div>

					{resultViewMode === 'table' ? (
						<>
							<div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
								<div className="overflow-x-auto">
									<table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
										<thead className="bg-gray-50 dark:bg-gray-800">
											<tr>
												{queryResult.columns.map(column => (
													<th
														key={column}
														className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase dark:text-gray-400"
													>
														{column}
													</th>
												))}
											</tr>
										</thead>
										<tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-900 dark:divide-gray-700">
											{queryResult.rows.slice(0, 10).map((row, index) => (
												<tr
													key={index}
													className={
														index % 2 === 0
															? 'bg-white dark:bg-gray-900'
															: 'bg-gray-50 dark:bg-gray-800'
													}
												>
													{queryResult.columns.map(column => (
														<td
															key={column}
															className="px-4 py-2 max-w-xs text-sm text-gray-900 truncate dark:text-gray-100"
														>
															{JSON.stringify(row[column])}
														</td>
													))}
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>

							{queryResult.rowCount > 10 && (
								<div className="text-xs text-center text-gray-500 dark:text-gray-400">
									Showing first 10 of {queryResult.rowCount} rows
								</div>
							)}
						</>
					) : (
						<LazyJsonPreview
							data={queryResult.rows}
							maxItems={20}
							maxDepth={3}
							label={`Query Results (${queryResult.rowCount} rows)`}
							defaultVisible={true}
						/>
					)}
				</div>
			)}

			{/* Query Error */}
			{queryError && (
				<div className="p-3 bg-red-50 rounded-lg border border-red-200 dark:bg-red-900/20 dark:border-red-800">
					<div className="text-sm font-medium text-red-700 dark:text-red-200">
						Query Execution Error:
					</div>
					<div className="mt-1 text-sm text-red-600 dark:text-red-300">{queryError}</div>
				</div>
			)}

			{/* Output Structure */}
			<div className="p-3 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
				<h5 className="mb-2 text-sm font-medium text-blue-900 dark:text-blue-200">
					Output Structure
				</h5>
				<div className="space-y-1 text-xs text-blue-800 dark:text-blue-300">
					<div>The query results will be available to downstream nodes as:</div>
					<div className="ml-4 space-y-1 font-mono">
						<div>{'{{database.rows}}'} - Array of all result rows</div>
						<div>{'{{database.first}}'} - First result row (or null)</div>
						<div>{'{{database.rowCount}}'} - Number of rows returned</div>
						<div>{'{{database.first.columnName}}'} - Access specific column values</div>
					</div>
				</div>
			</div>
		</div>
	);

	// Step configurations
	const steps = [
		{
			id: 1,
			title: 'Connect',
			description: 'Configure database connection settings',
			icon: Database,
			isValid: validateStep(1),
		},
		{
			id: 2,
			title: 'Design',
			description: 'Choose operation and configure your database action',
			icon: Table,
			isValid: validateStep(2),
		},
		{
			id: 3,
			title: 'Preview & Run',
			description: 'Test your configuration and preview results',
			icon: Eye,
			isValid: validateStep(3),
		},
	];

	const renderStepIndicator = () => (
		<div className="flex justify-between items-center px-2 mb-6">
			{steps.map((step, index) => (
				<div key={step.id} className="flex flex-1 items-center">
					<button
						onClick={() => {
							// Allow navigation to current step or completed steps
							if (step.id <= currentStep || completedSteps.has(step.id)) {
								setCurrentStep(step.id);
							}
						}}
						disabled={step.id > currentStep && !completedSteps.has(step.id)}
						className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-200 w-full ${
							currentStep === step.id
								? 'bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800'
								: completedSteps.has(step.id)
								? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/30 cursor-pointer'
								: step.id < currentStep
								? 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer'
								: 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed'
						} ${
							step.id <= currentStep || completedSteps.has(step.id)
								? ''
								: 'pointer-events-none'
						}`}
					>
						<div
							className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
								completedSteps.has(step.id)
									? 'bg-green-500 dark:bg-green-600 text-white'
									: currentStep === step.id
									? 'bg-blue-500 dark:bg-blue-600 text-white'
									: 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
							}`}
						>
							{completedSteps.has(step.id) ? (
								<Check className="w-4 h-4" />
							) : (
								<step.icon className="w-4 h-4" />
							)}
						</div>
						<div className="flex-1 min-w-0 text-left">
							<div
								className={`text-sm font-medium truncate ${
									currentStep === step.id
										? 'text-blue-900 dark:text-blue-100'
										: completedSteps.has(step.id)
										? 'text-green-900 dark:text-green-100'
										: 'text-gray-700 dark:text-gray-300'
								}`}
							>
								{step.title}
							</div>
							<div
								className={`text-xs truncate ${
									currentStep === step.id
										? 'text-blue-600 dark:text-blue-300'
										: completedSteps.has(step.id)
										? 'text-green-600 dark:text-green-300'
										: 'text-gray-500 dark:text-gray-400'
								}`}
							>
								{step.description}
							</div>
						</div>
					</button>
					{index < steps.length - 1 && (
						<div className="flex-shrink-0 mx-2 w-4 h-4 text-gray-400 dark:text-gray-500">
							→
						</div>
					)}
				</div>
			))}
		</div>
	);

	const renderCurrentStep = () => {
		switch (currentStep) {
			case 1:
				return renderConnectionTab();
			case 2:
				return renderDesignTab();
			case 3:
				return renderTestTab();
			default:
				return renderConnectionTab();
		}
	};

	/**
	 * Render the schema validation results in the modal
	 */
	const renderSchemaValidationResults = () => {
		if (!schemaValidationResult) return null;

		const { changePlan = [], message } = schemaValidationResult;

		if (changePlan.length === 0) {
			return (
				<div className="py-8 text-center">
					<div className="mb-4 text-4xl text-green-500 dark:text-green-400">✅</div>
					<h4 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">
						Schema is Up to Date
					</h4>
					<p className="text-sm text-gray-600 dark:text-gray-400">
						{message ||
							'No schema changes are needed. Your table structure matches the configuration.'}
					</p>
				</div>
			);
		}

		// Group steps by risk level
		const safeSteps = changePlan.filter((step: any) => step.riskLevel === 'SAFE');
		const riskySteps = changePlan.filter((step: any) => step.riskLevel === 'RISKY');
		const dangerousSteps = changePlan.filter((step: any) => step.riskLevel === 'DANGEROUS');

		const getRiskColor = (riskLevel: string) => {
			switch (riskLevel) {
				case 'SAFE':
					return 'text-green-600 dark:text-green-400';
				case 'RISKY':
					return 'text-yellow-600 dark:text-yellow-400';
				case 'DANGEROUS':
					return 'text-red-600 dark:text-red-400';
				default:
					return 'text-gray-600 dark:text-gray-400';
			}
		};

		const getRiskIcon = (riskLevel: string) => {
			switch (riskLevel) {
				case 'SAFE':
					return '✅';
				case 'RISKY':
					return '⚠️';
				case 'DANGEROUS':
					return '❌';
				default:
					return '❓';
			}
		};

		const getRiskBg = (riskLevel: string) => {
			switch (riskLevel) {
				case 'SAFE':
					return 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800';
				case 'RISKY':
					return 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800';
				case 'DANGEROUS':
					return 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800';
				default:
					return 'bg-gray-50 border-gray-200 dark:bg-gray-900/20 dark:border-gray-800';
			}
		};

		return (
			<div className="space-y-6">
				{/* Summary */}
				<div className="p-4 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
					<h4 className="mb-2 text-sm font-medium text-blue-900 dark:text-blue-200">
						Schema Changes Required
					</h4>
					<div className="grid grid-cols-3 gap-4 text-center">
						<div className="space-y-1">
							<div className="text-lg font-bold text-green-600 dark:text-green-400">
								{safeSteps.length}
							</div>
							<div className="text-xs text-gray-600 dark:text-gray-400">Safe</div>
						</div>
						<div className="space-y-1">
							<div className="text-lg font-bold text-yellow-600 dark:text-yellow-400">
								{riskySteps.length}
							</div>
							<div className="text-xs text-gray-600 dark:text-gray-400">Risky</div>
						</div>
						<div className="space-y-1">
							<div className="text-lg font-bold text-red-600 dark:text-red-400">
								{dangerousSteps.length}
							</div>
							<div className="text-xs text-gray-600 dark:text-gray-400">
								Dangerous
							</div>
						</div>
					</div>
				</div>

				{/* Change Plan Steps */}
				<div className="space-y-4">
					<h4 className="text-sm font-medium text-gray-900 dark:text-white">
						Planned Changes ({changePlan.length} steps)
					</h4>

					{changePlan.map((step: any, index: number) => (
						<div
							key={step.id}
							className={`p-4 rounded-lg border ${getRiskBg(step.riskLevel)}`}
						>
							<div className="flex gap-3 items-start">
								<div className="flex-shrink-0 mt-0.5">
									<span className="text-lg">{getRiskIcon(step.riskLevel)}</span>
								</div>
								<div className="flex-1 space-y-2">
									<div className="flex justify-between items-center">
										<h5 className="text-sm font-medium text-gray-900 dark:text-white">
											Step {index + 1}: {step.description || step.id}
										</h5>
										<span
											className={`text-xs font-medium px-2 py-1 rounded ${getRiskColor(
												step.riskLevel
											)}`}
										>
											{step.riskLevel}
										</span>
									</div>

									{step.warnings && step.warnings.length > 0 && (
										<div className="space-y-1">
											<p className="text-xs font-medium text-gray-700 dark:text-gray-300">
												Warnings:
											</p>
											<ul className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
												{step.warnings.map((warning: string, i: number) => (
													<li key={i} className="flex gap-2 items-start">
														<span className="text-yellow-500 dark:text-yellow-400 mt-0.5">
															•
														</span>
														{warning}
													</li>
												))}
											</ul>
										</div>
									)}

									{step.requiresConfirmation && (
										<div className="p-2 bg-red-100 rounded border border-red-200 dark:bg-red-900/30 dark:border-red-700">
											<p className="text-xs font-medium text-red-800 dark:text-red-200">
												⚠️ This step requires explicit confirmation
											</p>
										</div>
									)}

									<div className="flex gap-4 items-center text-xs text-gray-500 dark:text-gray-400">
										<span>Duration: ~{step.estimatedDuration || 1000}ms</span>
										<span>Online: {step.online ? 'Yes' : 'No'}</span>
										{step.canRetry && <span>Retryable: Yes</span>}
									</div>

									{/* Show SQL for advanced users */}
									<details className="mt-2">
										<summary className="text-xs font-medium text-gray-700 cursor-pointer dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
											Show SQL
										</summary>
										<div className="p-2 mt-2 font-mono text-xs text-gray-800 bg-gray-100 rounded dark:bg-gray-700 dark:text-gray-200">
											{step.sql}
										</div>
									</details>
								</div>
							</div>
						</div>
					))}
				</div>

				{/* Overall Impact Summary */}
				{(dangerousSteps.length > 0 || riskySteps.length > 0) && (
					<div className="p-4 bg-red-50 rounded-lg border border-red-200 dark:bg-red-900/20 dark:border-red-800">
						<h4 className="mb-2 text-sm font-medium text-red-900 dark:text-red-200">
							⚠️ Important Safety Information
						</h4>
						<div className="space-y-2 text-xs text-red-800 dark:text-red-300">
							{dangerousSteps.length > 0 && (
								<p>
									• {dangerousSteps.length} dangerous operation(s) detected -
									these may cause permanent data loss
								</p>
							)}
							{riskySteps.length > 0 && (
								<p>
									• {riskySteps.length} risky operation(s) detected - these may
									fail or cause temporary unavailability
								</p>
							)}
							<p>• Always backup your data before applying schema changes</p>
							<p>• Test these changes on a development environment first</p>
						</div>
					</div>
				)}
			</div>
		);
	};

	/**
	 * Handle executing schema changes (non-dry-run)
	 */
	const handleExecuteSchemaChanges = async () => {
		if (!config.createTableConfig?.table) return;

		setShowSchemaModal(false);
		setIsValidatingSchema(true);

		try {
			// Format connection to trigger secret resolution in DatabaseService.execute
			const connectionWithSecrets = {
				type: config.connection.type || 'postgres',
				mode: config.connection.mode || 'local-secrets',
				secretRef: config.connection.secretRef || config.connection.secretName,
				secretName: config.connection.secretName,
				// Don't include host/password to trigger secret resolution path
			};

			// Execute with dry-run disabled
			const executePayload = {
				connection: connectionWithSecrets,
				mode: 'CREATE_TABLE',
				payload: {
					table: config.createTableConfig.table,
					columns: config.createTableConfig.columns || [],
					indexes: config.createTableConfig.indexes || [],
					ifNotExists: config.createTableConfig.ifNotExists !== false,
					allowSchemaAlteration: true,
					dryRun: false, // Execute for real
				},
				options: {
					dryRun: false,
				},
			};

			const response = await fetch('/api/v1/db/execute', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
				},
				body: JSON.stringify(executePayload),
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(errorData.message || 'Schema execution failed');
			}

			const result = await response.json();

			// Check if the operation actually succeeded
			if (result.schemaChangeResult?.success === false) {
				// Operation failed - show error
				setConnectionStatus({
					success: false,
					message:
						result.message ||
						result.schemaChangeResult?.error ||
						'Schema changes failed',
				});
				setQueryError(
					`Schema alteration failed: ${
						result.schemaChangeResult?.error || 'Unknown error'
					}`
				);
			} else {
				// Operation succeeded - show success
				setConnectionStatus({
					success: true,
					message: result.message || 'Schema changes applied successfully!',
				});
			}
		} catch (error) {
			console.error('Schema execution error:', error);
			setQueryError(error instanceof Error ? error.message : 'Schema execution failed');
		} finally {
			setIsValidatingSchema(false);
		}
	};

	return (
		<div className="flex flex-col h-full">
			{/* Validation Results */}
			{(isValidating || lastValidationResult) && (
				<div className="px-6 py-4 bg-white border-b border-gray-200 dark:border-gray-700 dark:bg-gray-900">
					<ValidationResultDisplay
						errors={lastValidationResult?.errors || []}
						isValidating={isValidating}
					/>
				</div>
			)}

			<div className="flex-1 p-6 space-y-6 bg-white transition-colors dark:bg-gray-900">
				{/* Header */}
				<div className="text-center">
					<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
						Database Configuration
					</h3>
					<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
						Connect to a database and execute dynamic queries with upstream data
					</p>
				</div>

				{/* Step Indicator */}
				{renderStepIndicator()}

				{/* Step Content */}
				<div className="flex-1 min-h-0">{renderCurrentStep()}</div>
			</div>

			{/* Schema Validation Modal */}
			{showSchemaModal && schemaValidationResult && (
				<div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
					<div className="w-full max-w-4xl max-h-[90vh] mx-4 bg-white rounded-lg shadow-xl dark:bg-gray-800 overflow-hidden">
						{/* Modal Header */}
						<div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
							<div>
								<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
									Schema Validation Results
								</h3>
								<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
									Table: {config.createTableConfig?.table}
								</p>
							</div>
							<div className="flex gap-3 items-center">
								{/* View Mode Toggle */}
								<div className="flex p-1 bg-gray-100 rounded-lg dark:bg-gray-700">
									<button
										onClick={() => setSchemaViewMode('visual')}
										className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
											schemaViewMode === 'visual'
												? 'bg-white text-gray-900 shadow-sm dark:bg-gray-600 dark:text-gray-100'
												: 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
										}`}
									>
										Visual
									</button>
									<button
										onClick={() => setSchemaViewMode('json')}
										className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
											schemaViewMode === 'json'
												? 'bg-white text-gray-900 shadow-sm dark:bg-gray-600 dark:text-gray-100'
												: 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
										}`}
									>
										JSON
									</button>
								</div>
								<button
									onClick={() => setShowSchemaModal(false)}
									className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
								>
									<X className="w-5 h-5" />
								</button>
							</div>
						</div>

						{/* Modal Content */}
						<div className="p-6 overflow-y-auto max-h-[70vh]">
							{schemaViewMode === 'visual' ? (
								renderSchemaValidationResults()
							) : (
								<LazyJsonPreview
									data={schemaValidationResult}
									maxItems={50}
									maxDepth={4}
									label="Raw Schema Validation Data"
									defaultVisible={true}
								/>
							)}
						</div>

						{/* Modal Footer */}
						<div className="flex gap-3 justify-end p-6 border-t border-gray-200 dark:border-gray-700">
							<button
								onClick={() => setShowSchemaModal(false)}
								className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600"
							>
								Close
							</button>
							{schemaValidationResult?.changePlan?.length > 0 && (
								<button
									onClick={() => handleExecuteSchemaChanges()}
									className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg border border-transparent hover:bg-amber-700 dark:bg-amber-500 dark:hover:bg-amber-600"
								>
									Apply Changes
								</button>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
